import React from "react";
import { AlertTriangle } from "@/components/icons";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// One shared error state for every list/detail load failure. Always Dutch,
// always with a retry, and visually distinct from a genuinely empty list.
export const ErrorState = ({
  message = "Er ging iets mis bij het laden.",
  onRetry,
  testid = "load-error",
  className = "",
}) => (
  <Card className={`flex flex-col items-center gap-3 py-10 text-center ${className}`} data-testid={testid}>
    <AlertTriangle className="text-letop-tekst" size={20} />
    <p className="text-body text-destructive">{message}</p>
    {onRetry && (
      <Button variant="secondary" data-testid={`${testid}-retry`} onClick={onRetry}>
        Opnieuw proberen
      </Button>
    )}
  </Card>
);

// One shared empty state. Never a bare "geen resultaten": it says what this
// screen is for in the owner's own words, and offers the first step. The caller
// renders this INSTEAD of the table, so column headings never sit above nothing.
export const EmptyState = ({ title, purpose, action, testid = "empty-state", className = "" }) => (
  <Card className={`flex flex-col items-center gap-3 px-6 py-14 text-center ${className}`} data-testid={testid}>
    <p className="text-kop text-foreground">{title}</p>
    {purpose && <p className="max-w-md text-body text-muted-foreground">{purpose}</p>}
    {action && (
      <Button className="mt-1" data-testid={`${testid}-action`} onClick={action.onClick}>
        {action.icon && <action.icon size={18} className="mr-2" />}
        {action.label}
      </Button>
    )}
  </Card>
);

// Inline variant for panels that already sit inside a Card.
export const InlineEmpty = ({ title, purpose, action, testid = "empty-state" }) => (
  <div className="flex flex-col items-center gap-2 px-4 py-10 text-center" data-testid={testid}>
    <p className="text-body font-semibold text-foreground">{title}</p>
    {purpose && <p className="max-w-md text-body text-muted-foreground">{purpose}</p>}
    {action && (
      <Button variant="secondary" size="sm" className="mt-1" data-testid={`${testid}-action`} onClick={action.onClick}>
        {action.icon && <action.icon size={18} className="mr-2" />}
        {action.label}
      </Button>
    )}
  </div>
);

// Inline (non-card) variant for panels that already sit inside a Card.
export const InlineError = ({ message = "Er ging iets mis bij het laden.", onRetry, testid = "load-error" }) => (
  <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid={testid}>
    <AlertTriangle className="text-letop-tekst" size={20} />
    <p className="text-body text-destructive">{message}</p>
    {onRetry && (
      <Button variant="secondary" size="sm" data-testid={`${testid}-retry`} onClick={onRetry}>
        Opnieuw proberen
      </Button>
    )}
  </div>
);
